import { PlaceholderPlugin } from "@platejs/media/react";
import { useMemo, useState } from "react";
import {
  useEditorPlugin,
  useRemoveNodeButton,
  useElement,
  useEditorRef,
} from "platejs/react";
import styles from "../styles/SlateEditor.module.scss";
import { Popover } from "radix-ui";
import FontAwesome from "../../utilities/FontAwesome";
import { ButtonWrapper } from "../toolbar/buttons/_Button";
import { compressImage } from "../../../utils/DataUtilities";
import NotificationManager from "../../notifications/NotificationManager";
import { reportError } from "../../../utils/errorReporting";

export function MediaPlaceholder(props) {
  const { element } = props;
  const { api } = useEditorPlugin(PlaceholderPlugin);

  const imageSrc = useMemo(() => {
    const currentFile = api.placeholder.getUploadingFile(element.id as string);
    if (currentFile) {
      {
        return URL.createObjectURL(currentFile);
      }
    }
  }, []);

  if (!imageSrc) {
    return null;
  }

  return <PlateImageElement src={imageSrc} />;
}

export function PlateImageElement(props: { src: string; [key: string]: any }) {
  const [open, setOpen] = useState(false);
  const editor = useEditorRef();
  const element = useElement();
  const { props: buttonProps } = useRemoveNodeButton({ element });
  const [isEditingAltText, setIsEditingAltText] = useState(false);
  const [altText, setAltText] = useState<string>((element.alt as string) || "");

  return (
    <Popover.Root open={open}>
      <figure contentEditable={false} className={styles.figure}>
        {!altText && (
          <div>
            <FontAwesome prefix={"fas"} name={"fa-triangle-exclamation"} />
          </div>
        )}
        <Popover.Trigger asChild>
          <img
            {...props}
            onClick={() => setOpen((v) => !v)}
            style={{ border: open ? "2px solid black" : "none" }}
            src={props.src}
          />
        </Popover.Trigger>
      </figure>
      <Popover.Portal>
        <Popover.Content onPointerDownOutside={() => setOpen(false)}>
          <div className={styles.imageUploadPopover}>
            {isEditingAltText ? (
              <>
                <ButtonWrapper
                  onClick={() => {
                    setIsEditingAltText(false);
                  }}
                >
                  <FontAwesome
                    prefix={"fas"}
                    name={"fa-rotate-left"}
                    fixedWidth
                  />
                </ButtonWrapper>
                <input
                  type={"text"}
                  placeholder={"Alt Text"}
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                />
                <ButtonWrapper
                  onClick={() => {
                    editor.tf.setNodes(
                      {
                        alt: altText,
                      },
                      {
                        at: element,
                      },
                    );
                    setIsEditingAltText(false);
                  }}
                >
                  <FontAwesome prefix={"fas"} name={"fa-save"} fixedWidth />
                </ButtonWrapper>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept={"image/*"}
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    try {
                      // Compress to 4MB — @platejs/media's PlaceholderPlugin silently rejects larger images.
                      // Default is `uploadConfig.image.maxFileSize: "4MB"` (see node_modules/@platejs/media/dist/react/index.js).
                      //   uploadConfig: {
                      //   ...
                      //     image: {
                      //       maxFileCount: 3,
                      //           maxFileSize: "4MB",
                      //           mediaType: KEYS.img,
                      //           minFileCount: 1
                      //     },
                      //   ...
                      //   }
                      const compressed = await Promise.all(
                        Array.from(files).map((f) => compressImage(f, 4)),
                      );
                      const path = editor.api.findPath(element);
                      editor
                        .getTransforms(PlaceholderPlugin)
                        .insert.media(compressed as unknown as FileList);
                      editor.tf.removeNodes({ at: path });
                    } catch (err) {
                      reportError(err, { error_source: "handled" });
                      NotificationManager.error(
                        err instanceof Error
                          ? err.message
                          : "Image upload failed.",
                      );
                    }
                  }}
                  id={`${element.id}-media-placeholder`}
                  name={`${element.id}-media-placeholder`}
                />
                <ButtonWrapper
                  onClick={() => {
                    setIsEditingAltText(true);
                  }}
                >
                  Edit Alt Text
                </ButtonWrapper>
                <label
                  className={styles.button}
                  htmlFor={`${element.id}-media-placeholder`}
                >
                  <FontAwesome fixedWidth prefix={"fas"} name={"fa-upload"} />
                </label>
                <ButtonWrapper {...buttonProps}>
                  <FontAwesome fixedWidth prefix={"fas"} name={"fa-trash"} />
                </ButtonWrapper>
              </>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
